import type { DateRange, Project, TimeEntry } from './types';

export const secondsToClock = (seconds: number, compact = false) => {
  const safe = Math.max(0, Math.floor(seconds));
  const h = Math.floor(safe / 3600);
  const m = Math.floor((safe % 3600) / 60);
  const s = safe % 60;
  if (compact) return `${h}h ${String(m).padStart(2, '0')}m`;
  return [h, m, s].map((value) => String(value).padStart(2, '0')).join(':');
};

export const formatHours = (seconds: number, precision = 1) => `${(seconds / 3600).toFixed(precision)}h`;

export const money = (amount: number) => new Intl.NumberFormat('en-NL', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
}).format(amount);

export const shortDate = (iso: string) => new Intl.DateTimeFormat('en', {
  day: '2-digit', month: 'short',
}).format(new Date(iso));

export const timeOfDay = (iso: string) => new Intl.DateTimeFormat('en', {
  hour: '2-digit', minute: '2-digit', hour12: false,
}).format(new Date(iso));

export const dateKey = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const isInRange = (iso: string, range: DateRange) => {
  if (range === 'all') return true;
  const date = new Date(iso);
  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  if (range === 'this-week' || range === 'last-week') {
    const day = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - day - (range === 'last-week' ? 7 : 0));
    const end = new Date(start);
    end.setDate(end.getDate() + 7);
    return date >= start && date < end;
  }
  start.setDate(1);
  return date >= start;
};

export const getBillableValue = (entry: TimeEntry, projects: Project[]) => {
  const project = projects.find((item) => item.id === entry.projectId);
  return entry.billable && project ? (entry.duration / 3600) * project.hourlyRate : 0;
};

export const groupProjectTotals = (entries: TimeEntry[], projects: Project[]) => projects
  .map((project) => ({
    ...project,
    seconds: entries.filter((entry) => entry.projectId === project.id).reduce((sum, entry) => sum + entry.duration, 0),
  }))
  .filter((project) => project.seconds > 0)
  .sort((a, b) => b.seconds - a.seconds);

export const csvEscape = (value: string | number | boolean) => {
  const raw = String(value);
  return /[",\n]/.test(raw) ? `"${raw.replaceAll('"', '""')}"` : raw;
};

export const entriesToCsv = (entries: TimeEntry[], projects: Project[]) => {
  const header = ['Date', 'Description', 'Client', 'Project', 'Tags', 'Start', 'End', 'Duration (hours)', 'Billable', 'Amount (EUR)'];
  const rows = entries.map((entry) => {
    const project = projects.find((item) => item.id === entry.projectId);
    return [
      new Date(entry.start).toISOString().slice(0, 10), entry.description, project?.client ?? '', project?.name ?? '',
      entry.tags.join('; '), timeOfDay(entry.start), timeOfDay(entry.end), (entry.duration / 3600).toFixed(2),
      entry.billable, getBillableValue(entry, projects).toFixed(2),
    ].map(csvEscape).join(',');
  });
  return [header.join(','), ...rows].join('\n');
};

